import type { KgInsight } from '@api';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { handleDownloadDrawer } from '@terminal/views/insight-feed/utils/handleDownloadDrawer';
import { KgInsightHeaderAction } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/components/kg-insight-content/KgInsightHeaderAction';
import clsx from 'clsx';
import { ReactNode, RefCallback, useRef } from 'react';
import { useMatch } from 'react-router';

import styles from '../../KgInsight.module.scss';
import { useScrollSpy } from '../../useScrollSpy';
import { getKgInsightSectionNavItems } from '../../utils/kg-insight-utils';
import { SectionNav } from '../section-nav/SectionNav';

type KgInsightViewProps = {
  kgInsight?: KgInsight;
  subject: KgInsight['subject'];
  tabsSlot?: ReactNode;
  children?: (props: {
    setSectionRef: (id: string) => RefCallback<HTMLElement>;
  }) => ReactNode;
};

export const KgInsightView = ({
  kgInsight,
  tabsSlot,
  children,
  subject,
}: KgInsightViewProps) => {
  const isAssetOverviewPage = !!useMatch(appPaths.assetOverview);
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const printableContentRef = useRef<HTMLDivElement>(null);

  const handleDownload = () => {
    if (printableContentRef.current && kgInsight) {
      handleDownloadDrawer({
        contentToPrint: printableContentRef.current,
        id: kgInsight.id,
        title: kgInsight.title,
      });
    }
  };

  const sections = kgInsight
    ? getKgInsightSectionNavItems(kgInsight.category, kgInsight.sections)
    : [];
  const sectionIds = sections.map(section => section.id);

  const { activeSectionId, setSectionRef, scrollToSection } = useScrollSpy({
    scrollContainerRef,
    sectionIds,
  });

  return (
    <main
      className={clsx(styles.root, {
        [styles.unsetMaxWidth]: isAssetOverviewPage && isNewEarningsTabEnabled,
      })}
    >
      <div className={styles.content} ref={scrollContainerRef}>
        {subject && (
          <KgInsightHeaderAction
            subject={subject}
            source={POSTHOG_TRACKING_SOURCES.KG_INSIGHT_PAGE}
            onDownload={kgInsight ? handleDownload : undefined}
            contentWrapperRef={printableContentRef}
          />
        )}
        <div className={styles.printableContent} ref={printableContentRef}>
          {tabsSlot}
          {children?.({ setSectionRef })}
        </div>
      </div>
      {!!sections.length && (
        <aside className={styles.sideNav}>
          <SectionNav
            sections={sections}
            activeSectionId={activeSectionId}
            onSectionClick={scrollToSection}
            onDownload={
              kgInsight && isAssetOverviewPage ? handleDownload : undefined
            }
          />
        </aside>
      )}
    </main>
  );
};
